import MicroModal from 'micromodal';

/**
 * Clears the modal content of iframes ready for the next time it is opened.
 */
const clearModalContent = () => {
  const testimonialVideoEls = document.querySelectorAll(
    '.micromodal.testimonial .modal__content iframe',
  );
  testimonialVideoEls.forEach((iframe) => iframe.remove());
};

/**
 * Handles adding the relevant iFrame to the DOM when a user clicks on the play button.
 *
 * @param {Event} event - Click event.
 * @param {HTMLDivElement} testimonial - The testimonial block.
 * @returns {void}
 */
const addTestimonialIFrame = (event, testimonial) => {
  event.preventDefault();
  const { videoUrl } = testimonial.dataset;
  if (!videoUrl) return;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('src', videoUrl);
  iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture;');
  iframe.setAttribute('frameborder', 0);

  const testimonialModal = document.querySelector('.micromodal.testimonial .modal__content');
  testimonialModal.appendChild(iframe);
};

/**
 * Update the relevant aria labels to the buttons in each testimonial.
 *
 * @param {HTMLDivElement} testimonialGroup - The testimonial group to set the aria labels on.
 */
const generateAriaLabels = (testimonialGroup) => {
  const testimonials = [...testimonialGroup.querySelectorAll('.wp-block-bigbite-testimonial')];
  testimonials.forEach((testimonial, index) => {
    const playButton = testimonial.querySelector('button.bb_testimonial-videoPlay');
    const nextButton = testimonial.querySelector('button.bb_testimonial-videoNext');
    const isActive = testimonial.classList.contains('is-active');
    const playButtonLabel = isActive
      ? `Play testimonial ${index + 1} (current)`
      : `Play testimonial ${index + 1}`;

    playButton?.setAttribute('aria-label', playButtonLabel);
    nextButton?.setAttribute('aria-label', `Show testimonial ${index + 1}`);
  });
};

/**
 * Handles the shared logic between the next button and the mobile selectors that allows a new testimonial to become 'active'.
 *
 * @param {HTMLDivElement} newTestimonial - The testimonial that is to become active.
 */
const setNewActiveTestimonial = (newTestimonial) => {
  const testimonialGroup = newTestimonial.closest('.wp-block-bigbite-testimonial-group');
  const liveRegion = testimonialGroup.querySelector('.bb_testimonials-liveRegion');
  const currentTestimonial = testimonialGroup.querySelector(
    '.wp-block-bigbite-testimonial.is-active',
  );
  const mobileSelector = currentTestimonial.querySelector('.bb_testimonials-mobileSelector');
  const mobileSelectorButtons = mobileSelector.querySelectorAll('button');
  const playButton = newTestimonial.querySelector('button.bb_testimonial-videoPlay');
  const testimonialBlocks = testimonialGroup.querySelectorAll('.wp-block-bigbite-testimonial');
  const indexOfNewTestimonial = [...testimonialBlocks].indexOf(newTestimonial);

  currentTestimonial.classList.remove('is-active');
  currentTestimonial.removeChild(mobileSelector);

  newTestimonial.classList.add('is-active');
  newTestimonial.appendChild(mobileSelector);
  liveRegion.textContent = `Testimonial ${indexOfNewTestimonial + 1} of ${
    testimonialBlocks.length
  }`;
  mobileSelectorButtons.forEach((button, buttonIndex) => {
    if (buttonIndex === indexOfNewTestimonial) {
      button.classList.add('is-active');
      button.setAttribute('disabled', true);
    } else {
      button.classList.remove('is-active');
      button.removeAttribute('disabled');
    }
  });
  generateAriaLabels(testimonialGroup);
  playButton?.focus();
};

/**
 * Handle a button in a mobile selector being clicked on.
 *
 * @param {Event} event - Click event on the mobile selector button.
 */
const handleMobileSelectorButtonClick = (event) => {
  const button = event.target;
  const mobileSelector = button.closest('.bb_testimonials-mobileSelector');
  const indexOfButton = [...mobileSelector.querySelectorAll('button')].indexOf(button);
  const nearestTestimonial = button.closest('.wp-block-bigbite-testimonial-group');
  const testimonialBlocks = nearestTestimonial.querySelectorAll('.wp-block-bigbite-testimonial');
  const testimonialToLoad = testimonialBlocks[indexOfButton];
  setNewActiveTestimonial(testimonialToLoad);
};

/**
 * Adds the mobile controls to the testimonial that is initially active.
 *
 * @param {HTMLDivElement} testimonialGroup - The testimonial group to add the controls to.
 */
const generateMobileControls = (testimonialGroup) => {
  const testimonials = [...testimonialGroup.querySelectorAll('.wp-block-bigbite-testimonial')];
  const activeIndex = testimonials.findIndex((testimonial) =>
    testimonial.classList.contains('is-active'),
  );

  const mobileSelector = document.createElement('div');
  mobileSelector.classList.add('bb_testimonials-mobileSelector');
  for (let i = 0; i < testimonials.length; i += 1) {
    const button = document.createElement('button');
    button.setAttribute('role', 'button');
    button.setAttribute('aria-label', `Show testimonial ${i + 1}`);
    button.addEventListener('click', handleMobileSelectorButtonClick);
    if (i === activeIndex) {
      button.setAttribute('disabled', true);
      button.classList.add('is-active');
    }
    mobileSelector.appendChild(button);
  }

  testimonials[activeIndex].appendChild(mobileSelector);
};

// Main
window.addEventListener('DOMContentLoaded', () => {
  const testimonialBlocks = document.querySelectorAll('.wp-block-bigbite-testimonial');
  if (testimonialBlocks.length === 0) return;

  MicroModal.init({
    openTrigger: 'data-testimonial-modal-open',
    closeTrigger: 'data-testimonial-modal-close',
    onClose: clearModalContent,
    awaitOpenAnimation: true,
    awaitCloseAnimation: true,
    disableScroll: true,
  });

  const testimonialGroups = document.querySelectorAll('.wp-block-bigbite-testimonial-group');
  testimonialGroups.forEach((testimonialGroup) => {
    generateMobileControls(testimonialGroup);
    generateAriaLabels(testimonialGroup);
  });

  testimonialBlocks.forEach((testimonial) => {
    const testimonialPlayButton = testimonial.querySelector('button.bb_testimonial-videoPlay');
    testimonialPlayButton?.addEventListener('click', (event) =>
      addTestimonialIFrame(event, testimonial),
    );

    const testimonialNextButton = testimonial.querySelector('button.bb_testimonial-videoNext');
    testimonialNextButton?.addEventListener('click', () => {
      setNewActiveTestimonial(testimonial);
    });
  });
});
